import type { I18n, StatusStep } from "./advisoryApi";

// Formatting helpers for the Advisory area. Kept local so the workbench does
// not depend on other features' format modules.

/** First usable label from a QP i18n map (en_GB preferred). */
export function i18n(v: I18n | string | undefined, fallback = "—"): string {
  if (typeof v === "string") return v || fallback;
  if (!v) return fallback;
  return v.en_GB ?? v.en ?? Object.values(v).find(Boolean) ?? fallback;
}

export function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function money(v: unknown, currency = "NOK", digits = 0): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return `${currency} ${n.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function pct(v: unknown, digits = 1): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return `${n.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`;
}

/** SQLite `YYYY-MM-DD HH:MM:SS` (UTC, no zone) or ISO → Date. */
export function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const s = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso.replace(" ", "T") + "Z";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dateTime(iso: string | null | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";
}

export function dateOnly(iso: string | null | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString("en-GB", { dateStyle: "medium" }) : "—";
}

/** "3 min ago" style for the home table's last-touched column. */
export function relative(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return "—";
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return dateOnly(iso);
}

// ---- Advice lifecycle status ---------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  in_progress: "In progress",
  submitted: "Submitted",
  pdf_generated: "Document ready",
  compliance_review: "Compliance review",
  external_review: "External review",
  signing: "Signing",
  completed: "Completed",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  failed: "Failed",
};

export function statusLabel(s: string | null | undefined): string {
  if (!s) return "—";
  return STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

/** CSS-safe pill modifier (colours live in advisory.css). */
export function statusPill(s: string | null | undefined): string {
  return `pill adv-status adv-status-${s && /^[a-z_]+$/.test(s) ? s : "draft"}`;
}

/** Still editable in the workbench (before submit). */
export function isEditable(s: string | null | undefined): boolean {
  return s === "draft" || s === "in_progress";
}

/** After submit, still moving (poll it). */
export function isInFlight(s: string | null | undefined): boolean {
  return (
    s === "submitted" ||
    s === "pdf_generated" ||
    s === "compliance_review" ||
    s === "external_review" ||
    s === "signing"
  );
}

// ---- Step naming ---------------------------------------------------------------------

const COMPONENT_LABEL: Record<string, string> = {
  field: "Field",
  goal_input: "Goal",
  portfolio_input: "Portfolio",
  custom_portfolio: "Custom portfolio",
  risk_assessment: "Risk assessment",
  financial_situation: "Financial situation",
  product_assessment: "Knowledge & experience",
  sustainability: "Sustainability preferences",
  sustainability_status: "Sustainability match",
  client_classification: "Client classification",
  client_preferences: "Client preferences",
  trapets_lookup: "AML screening",
  enin_lookup: "Company screening",
  wealth_plan: "Wealth plan",
  portfolio_display: "Holdings",
  portfolio_forecast: "Forecast",
  expected_value: "Expected value",
  historical_return: "Historical return",
  cost_chart: "Costs",
  asset_allocation: "Asset allocation",
  portfolio_xray: "X-ray",
  instance_snapshot: "Advice totals",
  advisor_note: "Advisor note",
};

export function stepLabel(s: StatusStep): string {
  if (s.kind === "field") return s.fieldName ?? "Field";
  const t = s.componentType ?? s.kind;
  return COMPONENT_LABEL[t] ?? t.replace(/_/g, " ");
}

// QP's `reason` codes on an open step, in the advisor's words.
const REASON_TEXT: Record<string, string> = {
  baseline: "Needs the client's figures.",
  risk_bound_breach: "The holdings sit outside the risk bounds for this goal's score.",
  no_products: "Add at least one product.",
  no_risk_score: "Complete the risk assessment first.",
  no_portfolio_for_score: "No model portfolio matches this risk score.",
  deviation: "The portfolio does not match the sustainability preferences.",
};

export function reasonText(reason: string | undefined): string | null {
  if (!reason) return null;
  return REASON_TEXT[reason] ?? reason.replace(/_/g, " ");
}

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
